import React, { useState, useEffect } from 'react';
import { Modal, Field, ModalFooter } from './modalComponents';
import { fetchFuelData } from './api';

const TruckRefillModal = ({ isOpen, onClose, truck, onRefill, saving }) => {
  const [form, setForm] = useState({
    gallons: '',
    source_tank_type: 'station',
    truck_meter_before: '',
    truck_meter_after: '',
    staff_id: '',
    notes: '',
  });
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  useEffect(() => {
    if (isOpen && truck) {
      const loadInventory = async () => {
        setLoadingInventory(true);
        try {
          const data = await fetchFuelData();
          setInventory(data.inventory);
        } catch (err) {
          console.error('Failed to load inventory', err);
        } finally {
          setLoadingInventory(false);
        }
      };
      loadInventory();
    }
  }, [isOpen, truck]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        gallons: '',
        source_tank_type: 'station',
        truck_meter_before: '',
        truck_meter_after: '',
        staff_id: '',
        notes: '',
      });
    }
  }, [isOpen]);

  const setFormField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const tank = inventory.find(i => i.fuel_type === truck?.fuel_type);
  const availableStationFuel = tank?.current_volume_gallons || 0;
  const maxRefill = truck ? truck.capacity_gallons - truck.current_volume_gallons : 0;

  const handleSubmit = () => {
    if (!form.gallons || parseFloat(form.gallons) <= 0) {
      alert('Please enter a valid number of gallons');
      return;
    }
    if (parseFloat(form.gallons) > maxRefill) {
      alert(`Cannot exceed truck capacity. Maximum refill: ${maxRefill} gallons`);
      return;
    }
    if (form.source_tank_type === 'station' && parseFloat(form.gallons) > availableStationFuel) {
      alert(`Insufficient fuel in station tank. Available: ${availableStationFuel} gallons`);
      return;
    }
    onRefill(form);
  };

  if (!truck) return null;

  return (
    <Modal title={`Refuel Truck: ${truck.truck_number}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-gray-700/30 rounded-lg p-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-gray-500 text-xs">Current Fuel</p>
              <p className="text-white font-bold">{truck.current_volume_gallons} / {truck.capacity_gallons} gal</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Max Refill</p>
              <p className="text-green-400 font-bold">{maxRefill} gal</p>
            </div>
          </div>
        </div>

        <Field label="Source *">
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio"
                value="station"
                checked={form.source_tank_type === 'station'}
                onChange={e => setFormField('source_tank_type', e.target.value)}
                className="text-blue-500"
              />
              <span className="text-gray-300 text-sm">Station Tank</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio"
                value="delivery"
                checked={form.source_tank_type === 'delivery'}
                onChange={e => setFormField('source_tank_type', e.target.value)}
                className="text-blue-500"
              />
              <span className="text-gray-300 text-sm">Direct Delivery</span>
            </label>
          </div>
        </Field>

        {form.source_tank_type === 'station' && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 text-xs">
            <span className="text-gray-400">Available in station: </span>
            <span className="text-blue-400 font-bold">{availableStationFuel} gal</span>
            <span className="text-gray-400 ml-2">({truck?.fuel_type})</span>
          </div>
        )}

        <Field label="Gallons to add *">
          <input 
            type="number" 
            min="0" 
            max={maxRefill}
            step="10"
            placeholder="e.g., 500"
            value={form.gallons} 
            onChange={e => setFormField('gallons', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Meter Start (optional)">
            <input 
              type="number" 
              placeholder="e.g., 12345.6"
              value={form.truck_meter_before} 
              onChange={e => setFormField('truck_meter_before', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
            />
          </Field>
          <Field label="Meter End (optional)">
            <input 
              type="number" 
              placeholder="e.g., 12845.6"
              value={form.truck_meter_after} 
              onChange={e => setFormField('truck_meter_after', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
            />
          </Field>
        </div>

        <Field label="Notes">
          <textarea 
            placeholder="Additional notes..."
            value={form.notes} 
            onChange={e => setFormField('notes', e.target.value)}
            rows="2"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
          />
        </Field>
      </div>
      <ModalFooter 
        onCancel={onClose} 
        onSave={handleSubmit}
        saving={saving} 
        disabled={!form.gallons || parseFloat(form.gallons) <= 0}
        label="Record Refill" 
      />
    </Modal>
  );
};

export default TruckRefillModal;